import React, { useEffect, useRef, useState } from "react";
import { Building2, Languages, MessageSquareQuote, Users, Phone, Plus, Trash2, Loader2, CheckCircle2, Save, BookMarked, Upload, Route, GraduationCap, BookOpen } from "lucide-react";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import { Topbar } from "@/components/Topbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const LANG_FLAG = { en: "🇬🇧", ar: "🇸🇦" };

const SECTORS = [
  { id: "real_estate", label: "Real Estate" }, { id: "clinic", label: "Clinic / Healthcare" },
  { id: "retail", label: "Retail" }, { id: "logistics", label: "Logistics" },
  { id: "education", label: "Education" }, { id: "home_services", label: "Home Services" },
  { id: "travel", label: "Travel" }, { id: "general", label: "Other" },
];

export default function Settings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { business, updateBusiness, refresh } = useBusiness();
  const isManager = user?.role === "manager";

  const [name, setName] = useState("");
  const [sector, setSector] = useState("general");
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [routing, setRouting] = useState(false);
  const [learningEnabled, setLearningEnabled] = useState(true);
  const [contentPacksEnabled, setContentPacksEnabled] = useState(true);
  const [waNumber, setWaNumber] = useState("");
  const [replies, setReplies] = useState([]);
  const [team, setTeam] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [newGloss, setNewGloss] = useState({ term: "", translation: "", context: "general" });
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [newReply, setNewReply] = useState({ label: "", text: "", lang: "en" });
  const [newAgent, setNewAgent] = useState({ name: "", email: "", password: "", proficient_languages: ["en"] });

  useEffect(() => {
    if (business) {
      setName(business.name || "");
      setSector(business.sector || "general");
      setAutoTranslate(!!business.auto_translate_incoming);
      setRouting(!!business.language_routing);
      setLearningEnabled(business.learning_enabled !== false);
      setContentPacksEnabled(business.content_packs_enabled !== false);
      setWaNumber(business.whatsapp_number || "");
    }
  }, [business]);

  const loadReplies = () => api.get("/saved-replies").then(({ data }) => setReplies(data)).catch(() => {});
  const loadTeam = () => api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  const loadGlossary = () => api.get("/glossary").then(({ data }) => setGlossary(data)).catch(() => {});
  useEffect(() => { loadReplies(); loadTeam(); loadGlossary(); }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await updateBusiness({ name, sector, auto_translate_incoming: autoTranslate, language_routing: routing, learning_enabled: learningEnabled, content_packs_enabled: contentPacksEnabled });
      toast.success(t("saved"));
    } catch { toast.error("Could not save"); }
    finally { setSavingProfile(false); }
  };

  const addGloss = async () => {
    if (!newGloss.term.trim() || !newGloss.translation.trim()) return;
    try {
      const { data } = await api.post("/glossary", newGloss);
      setGlossary((g) => [data, ...g]);
      setNewGloss({ term: "", translation: "", context: "general" });
      toast.success(t("saved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add term"); }
  };
  const delGloss = async (id) => {
    await api.delete(`/glossary/${id}`);
    setGlossary((g) => g.filter((x) => x.id !== id));
  };
  const importGlossary = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 });
      const entries = [];
      for (const r of rows) {
        const [term, translation, context] = r;
        if (!term || !translation) continue;
        const tterm = String(term).trim().toLowerCase();
        if (tterm === "term") continue; // skip header row
        entries.push({ term: String(term).trim(), translation: String(translation).trim(), context: String(context || "general").trim() });
      }
      if (!entries.length) { toast.error("No valid rows (expected: term, translation, context)"); return; }
      const { data } = await api.post("/glossary/bulk", { entries });
      toast.success(`${data.inserted} terms imported`);
      loadGlossary();
    } catch (e) { toast.error("Could not import file"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleAgentLang = async (member, lang) => {
    const cur = member.proficient_languages || [];
    const next = cur.includes(lang) ? cur.filter((l) => l !== lang) : [...cur, lang];
    setTeam((ts) => ts.map((m) => (m.user_id === member.user_id ? { ...m, proficient_languages: next } : m)));
    try { await api.put(`/team/${member.user_id}/languages`, { proficient_languages: next }); }
    catch { toast.error("Could not update languages"); loadTeam(); }
  };

  const addReply = async () => {
    if (!newReply.label || !newReply.text) return;
    try {
      const { data } = await api.post("/saved-replies", newReply);
      setReplies((r) => [data, ...r]);
      setNewReply({ label: "", text: "", lang: "en" });
      toast.success(t("saved"));
    } catch { toast.error("Could not save reply"); }
  };
  const delReply = async (id) => {
    await api.delete(`/saved-replies/${id}`);
    setReplies((r) => r.filter((x) => x.id !== id));
  };

  const addAgent = async () => {
    if (!newAgent.name || !newAgent.email || newAgent.password.length < 6) {
      toast.error("Enter name, email and a 6+ char password");
      return;
    }
    try {
      await api.post("/team", { ...newAgent, role: "agent" });
      setNewAgent({ name: "", email: "", password: "", proficient_languages: ["en"] });
      loadTeam();
      toast.success("Agent added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add agent"); }
  };
  const delAgent = async (id) => {
    try { await api.delete(`/team/${id}`); loadTeam(); } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  const connectWhatsapp = async () => {
    try {
      await updateBusiness({ whatsapp_number: waNumber, whatsapp_connected: true });
      toast.success("WhatsApp connected (demo)");
    } catch { toast.error("Could not connect"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="font-head font-bold text-2xl sm:text-3xl text-foreground mb-6">{t("settings")}</h1>

        <Tabs defaultValue="profile">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="profile" data-testid="tab-profile"><Building2 size={15} className="me-1.5" /> {t("profile")}</TabsTrigger>
            <TabsTrigger value="replies" data-testid="tab-replies"><MessageSquareQuote size={15} className="me-1.5" /> {t("savedReplies")}</TabsTrigger>
            <TabsTrigger value="glossary" data-testid="tab-glossary"><BookMarked size={15} className="me-1.5" /> {t("glossary")}</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team"><Users size={15} className="me-1.5" /> {t("team")}</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><Phone size={15} className="me-1.5" /> {t("whatsapp")}</TabsTrigger>
          </TabsList>

          {/* Profile + translation */}
          <TabsContent value="profile" className="mt-5">
            <div className="bg-white border border-border rounded-2xl p-6 space-y-5">
              <div>
                <Label>{t("businessName")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isManager} className="h-11 mt-1.5" data-testid="settings-name" />
              </div>
              <div>
                <Label className="flex items-center gap-2"><Building2 size={15} /> {t("sector")}</Label>
                <Select value={sector} onValueChange={setSector} disabled={!isManager}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="settings-sector"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SECTORS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/40 border border-border">
                <div className="pe-4">
                  <div className="font-medium text-foreground flex items-center gap-2"><Languages size={16} /> {t("autoTranslateIncoming")}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{t("autoTranslateHint")}</div>
                </div>
                <Switch checked={autoTranslate} onCheckedChange={setAutoTranslate} disabled={!isManager} data-testid="settings-auto-translate" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/40 border border-border">
                <div className="pe-4">
                  <div className="font-medium text-foreground flex items-center gap-2"><Route size={16} /> {t("languageRouting")}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{t("languageRoutingHint")}</div>
                </div>
                <Switch checked={routing} onCheckedChange={setRouting} disabled={!isManager} data-testid="settings-routing" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/40 border border-border">
                <div className="pe-4">
                  <div className="font-medium text-foreground flex items-center gap-2"><GraduationCap size={16} /> {t("enableLearning")}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{t("enableLearningHint")}</div>
                </div>
                <Switch checked={learningEnabled} onCheckedChange={setLearningEnabled} disabled={!isManager} data-testid="settings-learning" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/40 border border-border">
                <div className="pe-4">
                  <div className="font-medium text-foreground flex items-center gap-2"><BookOpen size={16} /> {t("enableContentPacks")} <span className="text-[10px] font-semibold text-accent bg-accent/10 px-1.5 py-0.5 rounded">{t("proOnly")}</span></div>
                  <div className="text-sm text-muted-foreground mt-0.5">{t("enableContentPacksHint")}</div>
                </div>
                <Switch checked={contentPacksEnabled} onCheckedChange={setContentPacksEnabled} disabled={!isManager} data-testid="settings-content-packs" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                <div className="font-medium text-foreground">{t("planLabel")}</div>
                <span className="text-sm font-semibold uppercase text-primary bg-primary/10 px-3 py-1 rounded-full" data-testid="settings-plan-badge">{business?.plan || "free"}</span>
              </div>
              {isManager && (
                <Button onClick={saveProfile} disabled={savingProfile} className="rounded-xl bg-accent hover:bg-accent/90" data-testid="settings-save-profile">
                  {savingProfile ? <Loader2 className="animate-spin" /> : <><Save size={16} /> {t("save")}</>}
                </Button>
              )}
            </div>
          </TabsContent>

          {/* Saved replies */}
          <TabsContent value="replies" className="mt-5">
            <div className="bg-white border border-border rounded-2xl p-6">
              <div className="grid sm:grid-cols-[1fr_2fr_auto] gap-2 items-end">
                <div>
                  <Label>{t("replyLabel")}</Label>
                  <Input value={newReply.label} onChange={(e) => setNewReply((r) => ({ ...r, label: e.target.value }))} className="h-11 mt-1.5" data-testid="reply-label-input" />
                </div>
                <div>
                  <Label>{t("replyText")}</Label>
                  <Input value={newReply.text} onChange={(e) => setNewReply((r) => ({ ...r, text: e.target.value }))} dir="auto" className="h-11 mt-1.5" data-testid="reply-text-input" />
                </div>
                <Button onClick={addReply} className="h-11 rounded-xl bg-accent hover:bg-accent/90" data-testid="add-reply-btn"><Plus size={16} /> {t("addReply")}</Button>
              </div>
              <div className="mt-5 space-y-2">
                {replies.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border" data-testid={`saved-reply-${r.id}`}>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-primary uppercase tracking-wide">{r.label} · {r.lang.toUpperCase()}</div>
                      <div className="text-sm text-foreground truncate" dir="auto">{r.text}</div>
                    </div>
                    <button onClick={() => delReply(r.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`del-reply-${r.id}`}><Trash2 size={16} /></button>
                  </div>
                ))}
                {replies.length === 0 && <p className="text-sm text-muted-foreground">No saved replies yet.</p>}
              </div>
            </div>
          </TabsContent>

          {/* Glossary */}
          <TabsContent value="glossary" className="mt-5">
            <div className="bg-white border border-border rounded-2xl p-6">
              <p className="text-sm text-muted-foreground mb-4">{t("glossaryHint")}</p>
              {isManager && (
                <>
                  <div className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                    <div><Label>{t("glTerm")}</Label><Input value={newGloss.term} onChange={(e) => setNewGloss((g) => ({ ...g, term: e.target.value }))} placeholder="offer letter" className="h-11 mt-1.5" data-testid="gloss-term-input" /></div>
                    <div><Label>{t("glTranslation")}</Label><Input value={newGloss.translation} onChange={(e) => setNewGloss((g) => ({ ...g, translation: e.target.value }))} dir="auto" placeholder="خطاب العرض" className="h-11 mt-1.5" data-testid="gloss-translation-input" /></div>
                    <div><Label>{t("glContext")}</Label><Input value={newGloss.context} onChange={(e) => setNewGloss((g) => ({ ...g, context: e.target.value }))} placeholder="legal" className="h-11 mt-1.5 w-32" data-testid="gloss-context-input" /></div>
                    <Button onClick={addGloss} className="h-11 rounded-xl bg-accent hover:bg-accent/90" data-testid="add-gloss-btn"><Plus size={16} /> {t("add")}</Button>
                  </div>
                  <div className="mt-3">
                    <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => importGlossary(e.target.files?.[0])} data-testid="gloss-file-input" />
                    <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-xl" data-testid="gloss-import-btn">
                      {importing ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {t("bulkImport")}
                    </Button>
                    <span className="text-xs text-muted-foreground ms-3">{t("bulkImportHint")}</span>
                  </div>
                </>
              )}
              <div className="mt-5 space-y-2" data-testid="glossary-list">
                {glossary.map((g) => (
                  <div key={g.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border" data-testid={`gloss-${g.id}`}>
                    <div className="min-w-0 flex items-center gap-3 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{g.term}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="text-sm text-foreground" dir="auto">{g.translation}</span>
                      <span className="text-[10px] uppercase tracking-wide bg-secondary text-muted-foreground px-1.5 py-0.5 rounded">{g.context}</span>
                    </div>
                    {isManager && <button onClick={() => delGloss(g.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`del-gloss-${g.id}`}><Trash2 size={16} /></button>}
                  </div>
                ))}
                {glossary.length === 0 && <p className="text-sm text-muted-foreground">{t("glossaryEmpty")}</p>}
              </div>
            </div>
          </TabsContent>


          {/* Team */}
          <TabsContent value="team" className="mt-5">
            <div className="bg-white border border-border rounded-2xl p-6">
              {isManager && (
                <div className="mb-5 space-y-2">
                  <div className="grid sm:grid-cols-4 gap-2 items-end">
                    <div><Label>{t("agentName")}</Label><Input value={newAgent.name} onChange={(e) => setNewAgent((a) => ({ ...a, name: e.target.value }))} className="h-11 mt-1.5" data-testid="agent-name-input" /></div>
                    <div><Label>{t("email")}</Label><Input value={newAgent.email} onChange={(e) => setNewAgent((a) => ({ ...a, email: e.target.value }))} className="h-11 mt-1.5" data-testid="agent-email-input" /></div>
                    <div><Label>{t("password")}</Label><Input type="password" value={newAgent.password} onChange={(e) => setNewAgent((a) => ({ ...a, password: e.target.value }))} className="h-11 mt-1.5" data-testid="agent-password-input" /></div>
                    <Button onClick={addAgent} className="h-11 rounded-xl bg-accent hover:bg-accent/90" data-testid="add-agent-btn"><Plus size={16} /> {t("inviteAgent")}</Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t("proficientLanguages")}:</span>
                    {["en", "ar"].map((l) => {
                      const on = newAgent.proficient_languages.includes(l);
                      return (
                        <button key={l} type="button" data-testid={`new-agent-lang-${l}`}
                          onClick={() => setNewAgent((a) => ({ ...a, proficient_languages: on ? a.proficient_languages.filter((x) => x !== l) : [...a.proficient_languages, l] }))}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary text-muted-foreground border-transparent"}`}>
                          {LANG_FLAG[l]} {l.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {team.map((m) => (
                  <div key={m.user_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border" data-testid={`team-${m.user_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center font-semibold shrink-0">{m.name?.[0]}</div>
                      <div className="min-w-0">
                        <div className="font-medium text-foreground truncate">{m.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1" data-testid={`agent-langs-${m.user_id}`}>
                        {["en", "ar"].map((l) => {
                          const on = (m.proficient_languages || []).includes(l);
                          return (
                            <button key={l} type="button" disabled={!isManager}
                              onClick={() => toggleAgentLang(m, l)}
                              data-testid={`agent-lang-${m.user_id}-${l}`}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${on ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary text-muted-foreground border-transparent"} ${isManager ? "cursor-pointer" : "cursor-default"}`}>
                              {LANG_FLAG[l]} {l.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.role === "manager" ? "bg-primary/10 text-primary" : "bg-secondary text-foreground"}`}>
                        {m.role === "manager" ? t("manager") : t("agent")}
                      </span>
                      {isManager && m.role === "agent" && (
                        <button onClick={() => delAgent(m.user_id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-agent-${m.user_id}`}><Trash2 size={16} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* WhatsApp */}
          <TabsContent value="whatsapp" className="mt-5">
            <div className="bg-white border border-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                {business?.whatsapp_connected ? (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-accent"><CheckCircle2 size={18} /> {t("connected")}</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><Phone size={16} /> {t("notConnected")}</span>
                )}
              </div>
              <Label>{t("whatsappNumber")}</Label>
              <Input value={waNumber} onChange={(e) => setWaNumber(e.target.value)} placeholder="+971 50 000 0000" disabled={!isManager} className="h-11 mt-1.5 max-w-sm" data-testid="wa-number-input" />
              <p className="text-xs text-muted-foreground mt-2 max-w-lg">
                Official WhatsApp Business API connection (Meta) will activate two-way auto-sync. This is a simulated connection until Meta credentials are added.
              </p>
              {isManager && (
                <Button onClick={connectWhatsapp} className="mt-4 rounded-xl bg-[#25D366] hover:bg-[#1eb958] text-white" data-testid="connect-wa-btn">
                  <Phone size={16} /> {t("connectWhatsapp")}
                </Button>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
